<template>
    <ValidationObserver ref="observer" tag="div">
        <!-- ── Indumentarias disponibles ── -->
        <h3 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4">
            Indumentaria disponible
        </h3>

        <!-- Error: indumentarias obligatorias no añadidas -->
        <div
            ref="errorRef"
            v-if="obligatoriasError"
            class="tw:mb-4 tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-lg tw:text-sm tw:text-red-700 tw:flex tw:items-start tw:gap-2"
        >
            <svg class="tw:w-4 tw:h-4 tw:shrink-0 tw:mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"/>
            </svg>
            <span>{{ obligatoriasError }}</span>
        </div>

        <Skeleton
            v-if="indumentariasLoading"
            variant="tw:bg-transparent tw:shadow-none"
            padding="tw:p-0"
        >
            <template #skeleton>
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-8">
                    <div
                        v-for="n in 2"
                        :key="n"
                        class="tw:bg-white tw:border tw:border-slate-200 tw:rounded-xl tw:flex tw:flex-col tw:md:flex-row tw:animate-pulse"
                    >
                        <!-- Imagen skeleton: mobile full-width, desktop md:w-1/2 -->
                        <div class="tw:md:w-1/2 tw:aspect-square tw:bg-slate-100 tw:border-b tw:md:border-b-0 tw:md:border-r tw:border-slate-200"></div>
                        <!-- Controles skeleton -->
                        <div class="tw:p-5 tw:flex tw:flex-col tw:justify-between tw:flex-1 tw:gap-4">
                            <div class="tw:space-y-2">
                                <SkeletonItem height="tw:h-5" customClass="tw:w-3/4" />
                                <SkeletonItem height="tw:h-6" customClass="tw:w-1/3" />
                                <SkeletonItem height="tw:h-3" customClass="tw:w-1/2" />
                            </div>
                            <div class="tw:flex tw:flex-col tw:gap-3">
                                <div class="tw:flex tw:gap-3 ">
                                    <SkeletonItem height="tw:h-[42px]" customClass="tw:w-1/2" />
                                    <SkeletonItem height="tw:h-[42px]" customClass="tw:w-1/2"/>
                                </div>
                                <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Skeleton>

        <div v-else-if="indumentarias.length === 0" class="tw:text-center tw:py-8 tw:text-slate-500 tw:text-sm">
            No hay indumentarias disponibles para esta categoría.
        </div>

        <div v-else class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-8 tw:mb-8">
            <div
                v-for="item in indumentarias"
                :key="item.id"
                class="tw:bg-white tw:border tw:border-slate-200 tw:rounded-xl tw:hover:shadow-md tw:transition-shadow tw:flex tw:flex-col tw:md:flex-row"
            >
                <!-- Imagen -->
                <div class="tw:relative tw:md:w-1/2 tw:aspect-square tw:bg-slate-50 tw:flex tw:items-center tw:justify-center tw:p-6 tw:overflow-hidden tw:border-b tw:md:border-b-0 tw:md:border-r tw:border-slate-100">
                    <!-- Badge "Obligatoria" -->
                    <span
                        v-if="item.obligatoria"
                        class="tw:absolute tw:top-3 tw:left-3 tw:bg-red-600 tw:text-white tw:text-[10px] tw:font-bold tw:px-2 tw:py-0.5 tw:rounded tw:uppercase tw:tracking-wide tw:z-10"
                    >
                        Obligatoria
                    </span>
                    <img
                        v-if="item.imagen"
                        :src="item.imagen"
                        :alt="item.nombre"
                        class="tw:w-full tw:h-full tw:object-contain tw:mix-blend-multiply"
                        @error="onImgError($event)"
                    />
                    <svg
                        v-else
                        class="tw:w-20 tw:h-20 tw:text-slate-300"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1"
                        stroke="currentColor"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 8c0-1.1.9-2 2-2h1.5c.3 0 .6.1.8.4l.9 1.2c.2.3.5.4.8.4H18a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 13a2 2 0 100-4 2 2 0 000 4z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 15a2 2 0 100-4 2 2 0 000 4z"/>
                        <circle cx="12" cy="9" r="1" fill="currentColor"/>
                    </svg>
                </div>

                <!-- Controles -->
                <div class="tw:p-5 tw:flex tw:flex-col tw:justify-between tw:flex-1 tw:gap-4">
                    <div>
                        <h4 class="tw:text-base tw:font-bold tw:text-slate-800">{{ item.nombre }}</h4>
                        <div class="tw:mt-1">
                            <template v-if="item.incluida">
                                <span class="tw:text-slate-400 tw:line-through tw:text-sm">{{ formatPrecio(item.precio) }}</span>
                                <span class="tw:text-green-600 tw:font-bold tw:text-lg tw:ml-2">0 €</span>
                                <p class="tw:text-[11px] tw:text-slate-500 tw:mt-0.5">Máx. {{ item.cantidad_incluida }} incluidas</p>
                            </template>
                            <template v-else>
                                <span class="tw:text-primary tw:font-bold tw:text-lg">{{ formatPrecio(item.precio) }}</span>
                                <p class="tw:text-xs tw:text-slate-400">Precio por unidad (IVA incluido)</p>
                            </template>
                        </div>
                    </div>

                    <!-- Selectores: Talla + Cantidad -->
                    <div class="tw:flex tw:gap-3 tw:items-end">
                        <div class="tw:flex-1">
                            <BaseMultiselect
                                :value="getSel(item.id).tallaSelect?.id || null"
                                :options="tallasAsOptions(item)"
                                label="Talla"
                                name="'talla-' + index"
                                value-key="id"
                                label-key="nombre"
                                :multiple="false"
                                :disabled="!item.tallasSelect?.length"
                                @input="id => onTallaSelect(item, id)"
                            />
                        </div>
                        <div class="tw:flex-1">
                            <BaseMultiselect
                                :value="getSel(item.id).cantidadSelect || null"
                                :options="stockAsOptions(item)"
                                label="Uds"
                                name="'stock-' + index"
                                value-key="value"
                                label-key="label"
                                :multiple="false"
                                :disabled="!getSel(item.id).tallaSelect"
                                @input="val => onCantidadSelect(item, val)"
                            />
                        </div>
                    </div>

                    <!-- Botón Añadir -->
                    <Button
                        variant="danger"
                        customClass="tw:justify-center"
                        :disabled="!getSel(item.id).tallaSelect || !getSel(item.id).cantidadSelect"
                        @click="addIndumentaria(item)"
                    >
                        Añadir
                    </Button>
                </div>
            </div>
        </div>

        <!-- ── Indumentarias elegidas ── -->
        <div v-if="selected.length" class="tw:pt-6 tw:border-t tw:border-slate-200">
            <h3 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:mb-2">
                Indumentarias elegidas
            </h3>
            <p class="tw:text-xs tw:text-slate-500 tw:italic tw:mb-4">
                *Las indumentarias incluidas en la categoría se muestran sin coste. El resto se cobrarán.
            </p>

            <SimpleTable
                :flat="true"
                :columns="tableColumns"
                :data="selected"
                empty-text="No hay indumentarias seleccionadas"
                :default-col-width="defaultColWidth"
            >
                <template #cell-precio="{ row }">
                    <template v-if="row.incluida">
                        <span class="tw:text-slate-400 tw:line-through tw:text-xs">{{ formatPrecio(row.precio) }}</span>
                        <span class="tw:text-green-600 tw:font-bold tw:text-sm tw:ml-1">0 €</span>
                    </template>
                    <span v-else class="tw:text-slate-700 tw:font-medium">{{ formatPrecio(row.precio_total || row.precio * row.unidades) }}</span>
                </template>
                <template #cell-talla="{ row }">
                    <span class="tw:text-slate-700 tw:text-sm">{{ row.talla?.nombre }}</span>
                </template>
                <template #cell-imagen="{ row }">
                    <div class="tw:w-12 tw:h-12 tw:bg-slate-100 tw:rounded tw:overflow-hidden tw:flex tw:items-center tw:justify-center">
                        <img
                            v-if="row.imagen"
                            :src="row.imagen"
                            :alt="row.nombre"
                            class="tw:w-full tw:h-full tw:object-contain tw:mix-blend-multiply"
                            @error="onImgError($event)"
                        />
                        <svg
                            v-else
                            class="tw:w-6 tw:h-6 tw:text-slate-300"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 8c0-1.1.9-2 2-2h1.5c.3 0 .6.1.8.4l.9 1.2c.2.3.5.4.8.4H18a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8z"/>
                        </svg>
                    </div>
                </template>
                <template #cell-acciones="{ row }">
                    <button
                        type="button"
                        class="tw:text-red-500 tw:hover:text-red-700 tw:transition-colors tw:p-1"
                        @click="removeIndumentaria(row)"
                    >
                        <svg class="tw:w-5 tw:h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </template>
            </SimpleTable>
        </div>

        <!-- ── Campos personalizados (solo si hay indumentarias seleccionadas) ── -->
        <div v-if="selected.length" class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:mt-8">
            <BaseInputText
                v-model="campoNombre"
                label="Nombre"
                name="camiseta_nombre"
                placeholder="Ej: GARCÍA"
            />
            <BaseInputNumber
                v-model="campoNumero"
                label="Número"
                name="camiseta_numero"
                placeholder="Ej: 10"
            />
        </div>
    </ValidationObserver>
</template>

<script setup>
    import { ref, computed, inject, watch, nextTick } from 'vue';
    import filters from '@/filters';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue';
    import Skeleton from '@/modules/admin/ui/skeleton/Skeleton.vue';
    import SkeletonItem from '@/modules/admin/ui/skeleton/SkeletonItem.vue';

    const formData = inject('formData');

    // ── Props ──
    const props = defineProps({
        indumentarias: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        defaultColWidth: {
            type: String,
            default: '140px'
        }
    });

    // ── Estado ──
    const observer = ref(null);
    const errorRef = ref(null);
    const selected = ref([]);
    const campoNombre = ref(formData?.camiseta_nombre || '');
    const campoNumero = ref(formData?.camiseta_numero || '');

    // ── Selecciones por item (ref + inmutable: siempre reemplazo total) ──
    //    { [itemId]: { tallaSelect: object|null, cantidadSelect: number|null } }
    const selections = ref({});

    function getSel(itemId) {
        return selections.value[itemId] || { tallaSelect: null, cantidadSelect: null };
    }

    function setSel(itemId, update) {
        const current = selections.value[itemId] || { tallaSelect: null, cantidadSelect: null };
        selections.value = {
            ...selections.value,
            [itemId]: { ...current, ...update },
        };
    }

    const indumentariasLoading = computed(() => props.loading);
    const obligatoriasError = ref('');

    // ── Scroll al error cuando se muestra ──
    watch(obligatoriasError, async newVal => {
        if (newVal) {
            await nextTick();
            errorRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    });

    // ── Columnas tabla ──
    const tableColumns = [
        { key: 'nombre', label: 'Nombre', class: 'tw:w-40 tw:truncate' },
        { key: 'precio', label: 'Precio UD', class: 'tw:w-24', centered: true },
        { key: 'talla', label: 'Talla', class: 'tw:w-20', centered: true },
        { key: 'unidades', label: 'Cantidad', class: 'tw:w-20', centered: true },
        { key: 'imagen', label: 'Imagen', class: 'tw:w-20', centered: true },
        { key: 'acciones', label: '', class: 'tw:w-20', centered: true },
    ];

    // ── Helpers ──
    const formatPrecio = filters.formatear_precio;

    function tallasAsOptions(item) {
        return (item.tallasSelect || []).map(t => ({ id: t.id, nombre: t.nombre }));
    }

    function stockAsOptions(item) {
        const talla = getSel(item.id).tallaSelect;
        if (!talla || !talla.stockArray) return [];
        return talla.stockArray.map(n => ({ value: n, label: String(n) }));
    }

    function onImgError(e) {
        e.target.style.display = 'none';
    }

    // ── Handlers ──
    function onTallaSelect(item, id) {
        const talla = (item.tallasSelect || []).find(t => t.id === id) || null;
        setSel(item.id, { tallaSelect: talla, cantidadSelect: null });
    }

    function onCantidadSelect(item, val) {
        setSel(item.id, { cantidadSelect: val });
    }

    // ── Add / Remove ──
    let nextKey = 1;
    function addIndumentaria(item) {
        const sel = getSel(item.id);
        const talla = sel.tallaSelect;
        const cantidad = sel.cantidadSelect;
        if (!talla || !cantidad) return;

        // Eliminar entry previa de item + talla (misma combinación)
        while (selected.value.some(s => s.id === item.id && s.talla.id === talla.id)) {
            const idx = selected.value.findIndex(s => s.id === item.id && s.talla.id === talla.id);
            selected.value.splice(idx, 1);
        }

        if (item.incluida && !selected.value.some(s => s.id === item.id && s.incluida)) {
            const incluidas = Math.min(cantidad, item.cantidad_incluida || 0);
            selected.value.push({
                id: item.id, _key: nextKey++, talla: { id: talla.id, nombre: talla.nombre },
                unidades: incluidas, precio: item.precio, precio_total: 0,
                incluida: true, nombre: item.nombre, imagen: item.imagen,
            });
            const resto = cantidad - incluidas;
            if (resto > 0) {
                selected.value.push({
                    id: item.id, _key: nextKey++, talla: { id: talla.id, nombre: talla.nombre },
                    unidades: resto, precio: item.precio, precio_total: item.precio * resto,
                    incluida: false, nombre: item.nombre, imagen: item.imagen,
                });
            }
        } else {
            selected.value.push({
                id: item.id, _key: nextKey++, talla: { id: talla.id, nombre: talla.nombre },
                unidades: cantidad, precio: item.precio, precio_total: item.precio * cantidad,
                incluida: false, nombre: item.nombre, imagen: item.imagen,
            });
        }

        // Limpiar selección después de añadir
        setSel(item.id, { tallaSelect: null, cantidadSelect: null });
    }

    function removeIndumentaria(row) {
        const idx = selected.value.findIndex(
            s => s.id === row.id && s.talla.id === row.talla.id && s.unidades === row.unidades && s.incluida === row.incluida
        );
        if (idx !== -1) selected.value.splice(idx, 1);
    }

    async function validate() {
        const obligatorias = props.indumentarias.filter(i => i.obligatoria);
        const faltantes = obligatorias.filter(obl => {
            const enSeleccion = selected.value.filter(s => s.id === obl.id);
            const totalUnidades = enSeleccion.reduce((sum, s) => sum + s.unidades, 0);
            return totalUnidades < (obl.cantidad_incluida || 1);
        });

        if (faltantes.length > 0) {
            const names = faltantes.map(i => i.nombre).join(', ');
            const prefix = faltantes.length > 1 ? 's' : '';
            obligatoriasError.value = `Debe añadir la${prefix} indumentaria${prefix} obligatoria${prefix}: ${names}`;
            return false;
        }

        obligatoriasError.value = '';
        return true;
    }

    function getData() {
        return {
            selected: [...selected.value],
            campoNombre: campoNombre.value,
            campoNumero: campoNumero.value,
        };
    }

    defineExpose({
        get observer() { return observer.value; },
        validate,
        getData,
    });
</script>
